/**
 * Surface 2 — Confirmation ("We got your concern"). Implements confirmation/spec.md: a mono,
 * receipt-style tracking-code hero (tear-off dashed edge), a conditional save-emphasis callout
 * (warning for anonymous — the code is unrecoverable; info when contact was given), copy actions,
 * and a "what happens next" list. Focus moves to the headline on mount and the code is announced.
 *
 * EMBED behavior (report-form-fullpage-embed): a successful /report?embed=1&chrome=off submit
 * navigates here, so this page needs the same chromeless + resize treatment to render cleanly
 * inline — mirrors IntakePage/ContractorsDirectoryPage exactly. `chromeless` only suppresses
 * TownHeader's standalone wordmark (already a no-op here since TownHeader renders nothing in ANY
 * embed mode) and keeps the transparent-body + resize-on-mount behavior so the confirmation's
 * (different) height is reported to the host immediately after the swap.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/Button.js";
import { CheckIcon, AlertIcon, MailIcon, PrinterIcon } from "../components/icons.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { useEmbedResize } from "../components/useEmbedResize.js";
import { buildTrackingMailto } from "./confirmationMailto.js";

interface ConfirmationState {
  trackingCode: string;
  trackingUrl: string;
  hasContact: boolean;
  email?: string;
  /** Concern summary for the printed/saved record (confirmation/spec §4). Optional: a resident
   *  arriving via back-button may only have the code, so the print block degrades gracefully. */
  category?: string;
  address?: string;
  /** True when this confirmation follows a question submit (AskPage), not a concern report —
   *  swaps the copy to "question"/"answered" language (ask-a-question-faq.md §5.1 ♻️ confirmation
   *  screen). `category` doubles as the topic label in this case. */
  isQuestion?: boolean;
  /** True when this confirmation follows a records-request submit (RecordsRequestPage), not a
   *  concern report — swaps the copy to "records request"/"respond" language (records-request.md
   *  ♻️ confirmation screen). `category` doubles as the record-type label in this case. Mutually
   *  exclusive with `isQuestion`. */
  isKORA?: boolean;
  /** The tenant's KORA response-clock target + citation label (records-request.md), for the "what
   *  happens next" copy — only meaningful when `isKORA` is true. */
  koraRespondDays?: number;
  koraCitationLabel?: string;
  /** True when this confirmation follows a permit application submit (ApplyForPermitPage), not a
   *  concern report — swaps the copy to "permit application"/"review" language (Permits & Licensing,
   *  roadmap module 6 ♻️ confirmation screen). `category` doubles as the permit-type label in this
   *  case. Mutually exclusive with `isQuestion`/`isKORA`. */
  isPermit?: boolean;
}

interface Props {
  embed?: boolean;
  /** `?embed=1&chrome=off` only (App.tsx) — no-op when `embed` is false. Mirrors IntakePage's
   *  chromeless convention; this page has no separate page-heading to suppress (its `<h1>` IS the
   *  confirmation content), so chromeless here just keeps the transparent-body treatment. */
  chromeless?: boolean;
}

export function ConfirmationPage({ embed = false, chromeless = false }: Props): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useTheme();
  const state = location.state as ConfirmationState | null;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState<"code" | "link" | null>(null);
  // Print may be unavailable in some embedded contexts (confirmation/spec §5) — hide it then.
  const canPrint = typeof window !== "undefined" && typeof window.print === "function";
  const isChromeless = embed && chromeless;

  // Posts `towndesk:report-resize` on mount (and on any further height change) — reuses the same
  // message type as IntakePage since this is the IN-EMBED continuation of that same report flow,
  // not a separate embeddable surface with its own channel.
  useEmbedResize(embed, rootRef, "towndesk:report-resize");

  // CHROMELESS embed: iframe's own <html>/<body> still paint the cream `--td-paper` background
  // otherwise — identical gotcha/fix as IntakePage/MeetingsPage/ContractorsDirectoryPage.
  useEffect(() => {
    if (!isChromeless) return;
    const { documentElement: html, body } = document;
    html.classList.add("td-embed-transparent");
    body.classList.add("td-embed-transparent");
    return () => {
      html.classList.remove("td-embed-transparent");
      body.classList.remove("td-embed-transparent");
    };
  }, [isChromeless]);

  const confirmationPage = state?.isKORA
    ? "Records Request Received"
    : state?.isQuestion
      ? "Question Received"
      : state?.isPermit
        ? "Permit Application Received"
        : "Concern Received";
  useDocumentTitle(pageTitle(confirmationPage, config.townName));
  useEmbedClose(embed);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Back-button / direct hit with no state: send them to tracking rather than an empty card.
  if (!state?.trackingCode) {
    return <Navigate to="/report" replace />;
  }

  const {
    trackingCode,
    trackingUrl,
    hasContact,
    email,
    category,
    address,
    isQuestion,
    isKORA,
    koraRespondDays,
    koraCitationLabel,
    isPermit,
  } = state;
  const noun = isKORA ? "records request" : isQuestion ? "question" : isPermit ? "permit application" : "concern";

  const mailtoHref = buildTrackingMailto({
    townName: config.townName,
    trackingCode,
    trackingUrl,
  });

  const copy = async (text: string, which: "code" | "link") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard blocked (some iframes) — select the code so the user can Ctrl/Cmd-C.
      const el = document.getElementById("td-code-value");
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      setCopied(which);
      window.setTimeout(() => setCopied(null), 2500);
    }
  };

  const maskedEmail = email ? maskEmail(email) : undefined;

  return (
    <ResidentThemeRoot
      ref={rootRef}
      className={`td-intake${embed ? " td-intake--embed" : ""}${isChromeless ? " td-intake--chromeless" : ""}`}
    >
      <TownHeader embed={embed} />

      <main className="td-confirm">
        {/* Print-only record (confirmation/spec §4/§5): a clean single-purpose document when the
            resident chooses Print / Save as PDF. Hidden on screen; the @media print stylesheet
            hides the app chrome and shows only this. aria-hidden — SR users get the code from the
            live region above, this is a visual paper artifact. */}
        <div className="td-print-doc" aria-hidden="true">
          <p className="td-print-doc__town">{config.townName}</p>
          <p className="td-print-doc__kicker">
            {isKORA
              ? "Records request received"
              : isQuestion
                ? "Question received"
                : isPermit
                  ? "Permit application received"
                  : "Concern received"}
          </p>
          {(category || address) && (
            <dl className="td-print-doc__summary">
              {category ? (
                <div>
                  <dt>{isKORA ? "Record type" : isQuestion ? "Topic" : isPermit ? "Permit type" : "Concern"}</dt>
                  <dd>{category}</dd>
                </div>
              ) : null}
              {address ? (
                <div>
                  <dt>Location</dt>
                  <dd>{address}</dd>
                </div>
              ) : null}
            </dl>
          )}
          <p className="td-print-doc__label">Your tracking code</p>
          <p className="td-print-doc__code">{trackingCode}</p>
          <p className="td-print-doc__url">Check status: {trackingUrl}</p>
          <p className="td-print-doc__note">
            Keep this code — it's the only way to check an anonymous {noun} later.
          </p>
        </div>

        <div className="td-confirm__mark" aria-hidden="true">
          <CheckIcon width={28} height={28} />
        </div>
        <h1 className="td-confirm__title" tabIndex={-1} ref={headingRef}>
          {isKORA
            ? "We got your records request."
            : isQuestion
              ? "We got your question."
              : isPermit
                ? "We got your permit application."
                : "We got your concern."}
        </h1>
        <p className="td-confirm__sub">
          {isKORA
            ? `Thanks — we'll respond within ${koraRespondDays ?? 3} business days${
                koraCitationLabel ? ` under the ${koraCitationLabel}` : ""
              }. Here's your tracking code.`
            : isQuestion
              ? "Thanks — a staff member will answer. Here's your tracking code."
              : isPermit
                ? "Thanks — a staff member will review it. Here's your tracking code."
                : "Thanks — the City has it. Here's your tracking code."}
        </p>

        {/* Announce the code to screen readers without hunting (confirmation/spec §6). */}
        <p className="td-sr-only" aria-live="polite">
          {isKORA
            ? "Records request submitted."
            : isQuestion
              ? "Question submitted."
              : isPermit
                ? "Permit application submitted."
                : "Concern submitted."}{" "}
          Your tracking code is {spellOut(trackingCode)}.
        </p>

        <div className="td-receipt">
          <span className="td-receipt__label">Your tracking code</span>
          <span
            className="td-receipt__code"
            id="td-code-value"
            aria-label={`Tracking code ${spellOut(trackingCode)}`}
          >
            {trackingCode}
          </span>
          <div className="td-receipt__actions">
            <Button onClick={() => copy(trackingCode, "code")}>
              {copied === "code" ? "Copied ✓" : "Copy code"}
            </Button>
            <Button variant="secondary" onClick={() => copy(trackingUrl, "link")}>
              {copied === "link" ? "Copied ✓" : "Copy tracking link"}
            </Button>
            {/* Add to email: mailto: pre-filled with the code + track URL (confirmation/spec §3).
                An anchor (not a button) so it opens the OS mail client. */}
            <a className="td-btn td-btn--secondary" href={mailtoHref}>
              <MailIcon width={18} height={18} aria-hidden="true" />
              Add to email
            </a>
            {canPrint ? (
              <Button variant="secondary" onClick={() => window.print()}>
                <PrinterIcon width={18} height={18} aria-hidden="true" />
                Print / Save as PDF
              </Button>
            ) : null}
          </div>
        </div>

        {hasContact ? (
          <div className="td-callout td-callout--info" role="note" aria-labelledby="td-save-title">
            <p className="td-callout__title" id="td-save-title">
              <CheckIcon width={18} height={18} />
              We've emailed your code too.
            </p>
            <p>
              A copy is on its way{maskedEmail ? ` to ${maskedEmail}` : ""}. Saving the code is
              still handy. We'll email you when the status changes.
            </p>
          </div>
        ) : (
          <div
            className="td-callout td-callout--warning"
            role="note"
            aria-labelledby="td-save-title"
          >
            <p className="td-callout__title" id="td-save-title">
              <AlertIcon width={18} height={18} />
              Save this code now.
            </p>
            <p>
              It's the only way to check this {noun} later. We can't look it up or resend it —
              this{" "}
              {isKORA ? "request" : isQuestion ? "question" : isPermit ? "application" : "report"} is
              anonymous, so you won't get updates unless you check with the code.
            </p>
          </div>
        )}

        <section className="td-next" aria-labelledby="td-next-title">
          <h2 className="td-next__title" id="td-next-title">
            What happens next
          </h2>
          <ol className="td-next__list">
            {isKORA ? (
              <>
                <li>Received — the City has your records request.</li>
                <li>
                  A staff member responds within {koraRespondDays ?? 3} business days
                  {koraCitationLabel ? ` under the ${koraCitationLabel}` : ""}.
                </li>
                <li>Check status anytime with your code.</li>
              </>
            ) : isQuestion ? (
              <>
                <li>Received — the City has your question.</li>
                <li>A staff member answers it.</li>
                <li>Check the answer anytime with your code.</li>
              </>
            ) : isPermit ? (
              <>
                <li>Submitted — the City has your permit application.</li>
                <li>A staff member reviews it, then approves/denies and (if needed) inspects.</li>
                <li>Check status anytime with your code.</li>
              </>
            ) : (
              <>
                <li>Received — the City has your concern.</li>
                <li>A staff member reviews it.</li>
                <li>Check status anytime with your code.</li>
              </>
            )}
          </ol>
        </section>

        <div className="td-confirm__actions">
          <Button
            onClick={() =>
              navigate(`/track/${trackingCode}`, { state: { trackingUrl, trackingCode } })
            }
          >
            {isKORA
              ? "Check status"
              : isQuestion
                ? "Check for an answer"
                : isPermit
                  ? "Check status"
                  : "Check status"}
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                isKORA ? "/records-request" : isQuestion ? "/ask" : isPermit ? "/apply-permit" : "/report",
              )
            }
          >
            {isKORA
              ? "Submit another request"
              : isQuestion
                ? "Ask another question"
                : isPermit
                  ? "Apply for another permit"
                  : "Report another concern"}
          </Button>
        </div>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        <Link
          to={isKORA ? "/records-request" : isQuestion ? "/ask" : isPermit ? "/apply-permit" : "/report"}
        >
          {isKORA ? "Submit another" : isQuestion ? "Ask another" : isPermit ? "Apply again" : "Report another"}
        </Link>
      </footer>
    </ResidentThemeRoot>
  );
}

function maskEmail(email: string): string {
  const [user, domain] = email.split("@");
  if (!user || !domain) return email;
  return `${user[0]}•••@${domain}`;
}

/** Spell the code for screen readers so it's read char-by-char, not as a word. */
function spellOut(code: string): string {
  return code.split("").join(" ");
}
